import { useEffect } from 'react'
import { Link } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { useFamilyStore } from '@/stores/familyStore'
import { api, PendingItem } from '@/lib/api'
import { educationalAreaLabel } from '@/lib/constants'

// ── ForReviewSection ──────────────────────────────────────────────────────────

function PendingItemRow({ item, childName }: { item: PendingItem; childName: string | null }) {
  const isBook = item.itemType === 'book'
  const label = isBook ? 'Book' : 'Activity'
  const title = isBook ? item.title : item.description
  const subtitle = isBook
    ? [item.author, childName].filter(Boolean).join(' · ')
    : [item.educationalAreas.map(educationalAreaLabel).join(', '), childName].filter(Boolean).join(' · ')
  const to = isBook ? '/reading-list' : '/portfolio'

  return (
    <li className="flex items-center gap-3 py-2">
      {/* Thumbnail */}
      <div className="relative h-14 w-10 shrink-0">
        {item.mediaAttachments?.length > 0 ? (
          <img
            src={item.mediaAttachments[0]}
            alt={title}
            className="h-full w-full rounded object-cover"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center rounded bg-birch-100">
            {isBook ? (
              <svg className="h-4 w-4 text-birch-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
              </svg>
            ) : (
              <svg className="h-4 w-4 text-birch-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 3.741-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" />
              </svg>
            )}
          </div>
        )}
      </div>

      {/* Details */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900">{title}</p>
        <div className="mt-0.5 flex items-center gap-2">
          <span className="inline-flex shrink-0 items-center rounded-full bg-birch-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-birch-700">
            {label}
          </span>
          <p className="min-w-0 flex-1 truncate text-xs text-gray-400">{subtitle}</p>
          <Link
            to={to}
            className="ml-auto shrink-0 text-xs font-semibold text-amber-700 hover:text-amber-900"
          >
            Review →
          </Link>
        </div>
      </div>
    </li>
  )
}

function ForReviewSection({ familyId }: { familyId: string }) {
  const { family } = useFamilyStore()

  const { data: pending = [], isLoading } = useQuery<PendingItem[]>({
    queryKey: ['pending', familyId],
    queryFn: () => api.families.pending(familyId),
  })

  function childName(childId: string): string | null {
    return family?.children.find((c) => c.id === childId)?.firstName ?? null
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 shadow-sm">
      <div className="mb-1 flex items-center gap-2">
        <h2 className="text-base font-semibold text-amber-900">For Review</h2>
        {pending.length > 0 && (
          <span className="inline-flex items-center rounded-full bg-amber-200 px-2 py-0.5 text-xs font-bold text-amber-900">
            {pending.length}
          </span>
        )}
      </div>
      <p className="mb-3 text-xs text-amber-700">
        Content automatically catalogued by Birchfolio must be reviewed for accuracy before it will appear in the Portfolio.
      </p>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-amber-700">
          <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
          Loading…
        </div>
      ) : pending.length === 0 ? (
        <p className="text-sm text-amber-700">Nothing to review — you're all caught up!</p>
      ) : (
        <ul className="divide-y divide-amber-100">
          {pending.map((item) => (
            <PendingItemRow key={item.id} item={item} childName={childName(item.childId)} />
          ))}
        </ul>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const queryClient = useQueryClient()
  useEffect(() => { queryClient.invalidateQueries() }, [])

  const { user } = useAuthStore()
  const { family, activeChild, activeChildId } = useFamilyStore()

  const { data: profile } = useQuery({
    queryKey: ['me'],
    queryFn: api.me.get,
  })
  const familyId = profile?.familyId ?? null

  const { data: children = [] } = useQuery({
    queryKey: ['children', familyId],
    queryFn: () => api.children.list(familyId!),
    enabled: !!familyId,
  })

  const childId = (activeChildId && children.find((c) => c.id === activeChildId)?.id)
    ?? children[0]?.id
    ?? null

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['stats', familyId, childId],
    queryFn: () => api.children.stats(familyId!, childId!),
    enabled: !!familyId && !!childId,
  })

  const metricCards = [
    { label: 'Activities this week', value: stats?.activitiesThisWeek },
    { label: 'Books this week', value: stats?.booksThisWeek },
    { label: 'Topics covered this week', value: stats?.topicsCoveredThisWeek },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back{user ? `, ${user.givenName ?? user.email.split('@')[0]}` : ''}!
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {familyId
            ? family
              ? `Managing ${family.children.length} student${family.children.length !== 1 ? 's' : ''}`
              : null
            : <><Link to="/family" className="font-medium text-birch-600 hover:underline">Set up your family</Link> to get started</>}
        </p>
      </div>

      {activeChild && (
        <div className="rounded-xl border border-birch-100 bg-birch-50 p-6">
          <h2 className="text-lg font-semibold text-birch-800">
            Active student: {activeChild.firstName}
          </h2>
          {activeChild.dateOfBirth && (
            <p className="text-sm text-birch-600">
              Born {new Date(activeChild.dateOfBirth).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {metricCards.map(({ label, value }) => (
          <div
            key={label}
            className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
          >
            <p className="text-sm font-medium text-gray-500">{label}</p>
            <p className="mt-1 text-3xl font-bold text-gray-900">
              {statsLoading ? (
                <span className="inline-block h-8 w-10 animate-pulse rounded bg-gray-200" />
              ) : (value ?? '—')}
            </p>
          </div>
        ))}
      </div>

      {familyId && <ForReviewSection familyId={familyId} />}
    </div>
  )
}
